import * as THREE from 'three';
import { CLIFF_FLUX_CAP, REPOSE_FRAG, SPECKLE_DIFFUSE_RATE, SPECKLE_HEIGHT } from './repose';
import {
  CLEAR_FRAG,
  COMPOSE_FRAG,
  HF_PREVIEW_FRAG,
  SAMPLE_FRAG,
  SIM_VERT,
  SPLAT_FRAG,
  SPLAT_VERT,
} from './shaders';
import {
  CHUNK_SPLAT_KERNEL_INV_SUM,
  CHUNK_SPLAT_KERNEL_SHARPNESS,
  CHUNK_SPLAT_POINT_SIZE,
  CHUNK_SPLAT_VISUAL_GAIN,
  FINE_SPLAT_KERNEL_INV_SUM,
  FINE_SPLAT_KERNEL_SHARPNESS,
  FINE_SPLAT_POINT_SIZE,
  FINE_SPLAT_VISUAL_GAIN,
  GRIT_SPLAT_KERNEL_INV_SUM,
  GRIT_SPLAT_KERNEL_SHARPNESS,
  GRIT_SPLAT_POINT_SIZE,
  SPLAT_KERNEL_INV_SUM,
  SPLAT_KERNEL_SHARPNESS,
  SPLAT_POINT_SIZE,
  SPLAT_VISUAL_GAIN,
} from './splat';
// FINE_SPLAT_* distance/haze constants live in splat.ts and are mirrored in
// SPLAT_VERT — keep the GLSL literals in sync when retuning.
import { makeFloatTarget, makeHalfTarget, uploadFull } from './gpuUtils';
import type { ArenaXZ, BoxCollider } from './types';
import { bakeBaseElevation, sampleBaseElevation } from './baseElevation';

export type { ArenaXZ };

export interface HeightSample {
  /** Visual deposit = granular + film. */
  deposit: number;
  granular: number;
  film: number;
  base: number;
  mix: number;
  surface: number;
}

export interface HeightfieldSim {
  readonly size: number;
  readonly cellSize: number;
  readonly cellArea: number;
  texture(): THREE.Texture;
  baseTexture(): THREE.Texture;
  sampleAt(renderer: THREE.WebGLRenderer, x: number, z: number): HeightSample;
  splat(
    renderer: THREE.WebGLRenderer,
    posTex: THREE.Texture,
    metaTex: THREE.Texture,
    grainTexSize: number,
    capacity: number,
    originTex?: THREE.Texture | null,
  ): void;
  splatFines(
    renderer: THREE.WebGLRenderer,
    posTex: THREE.Texture,
    metaTex: THREE.Texture,
    fineTexSize: number,
    capacity: number,
    originTex?: THREE.Texture | null,
    velTex?: THREE.Texture | null,
    visualGain?: number,
  ): void;
  splatVolume(
    renderer: THREE.WebGLRenderer,
    x: number,
    y: number,
    z: number,
    mass: number,
    materialId?: number,
  ): void;
  repose(renderer: THREE.WebGLRenderer): void;
  reset(renderer: THREE.WebGLRenderer): void;
  blitPreview(renderer: THREE.WebGLRenderer, canvas: HTMLCanvasElement): void;
  dispose(): void;
}

export function createHeightfield(
  renderer: THREE.WebGLRenderer,
  size: number,
  arena: ArenaXZ,
  colliders: readonly BoxCollider[] = [],
): HeightfieldSim {
  const baseData = bakeBaseElevation(size, arena, colliders);
  // Upload via the same float-RT path as grain state so sampling is reliable
  // (a raw DataTexture can stay unbound on the first sim ticks).
  const baseRt = makeFloatTarget(size, THREE.NearestFilter);
  const baseRgba = new Float32Array(size * size * 4);
  for (let i = 0; i < size * size; i += 1) baseRgba[i * 4] = baseData[i];
  uploadFull(renderer, baseRt, size, baseRgba);
  const baseTex = baseRt.texture;
  const a = makeHalfTarget(size, THREE.LinearFilter);
  const b = makeHalfTarget(size, THREE.LinearFilter);
  const delta = makeHalfTarget(size, THREE.LinearFilter);
  const preview = makeHalfTarget(Math.min(size, 160), THREE.LinearFilter);
  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(a);
  renderer.setRenderTarget(b);
  renderer.setRenderTarget(delta);
  renderer.setRenderTarget(preview);
  renderer.setRenderTarget(prev);

  let read = a;
  let write = b;

  const cellSize = Math.min(arena.sizeX, arena.sizeZ) / size;
  const cellArea = (arena.sizeX / size) * (arena.sizeZ / size);

  const dummyOrigin = makeFloatTarget(1, THREE.NearestFilter);
  const dummyVel = makeFloatTarget(1, THREE.NearestFilter);
  const splatUniforms = {
    tPos: { value: null as THREE.Texture | null },
    tMeta: { value: null as THREE.Texture | null },
    tOrigin: { value: dummyOrigin.texture as THREE.Texture },
    tVel: { value: dummyVel.texture as THREE.Texture },
    uTexSize: { value: 256 },
    uArenaMin: { value: new THREE.Vector2(arena.minX, arena.minZ) },
    uArenaSize: { value: new THREE.Vector2(arena.sizeX, arena.sizeZ) },
    uCellArea: { value: cellArea },
    uVisualGain: { value: SPLAT_VISUAL_GAIN },
    uPointSize: { value: SPLAT_POINT_SIZE },
    uKernelInvSum: { value: SPLAT_KERNEL_INV_SUM },
    uKernelSharpness: { value: SPLAT_KERNEL_SHARPNESS },
    uGritPointSize: { value: GRIT_SPLAT_POINT_SIZE },
    uGritInvSum: { value: GRIT_SPLAT_KERNEL_INV_SUM },
    uGritSharpness: { value: GRIT_SPLAT_KERNEL_SHARPNESS },
    uSplatMode: { value: 0 },
    uFineAttenuate: { value: 0 },
    uGrainAttenuate: { value: 0 },
    uFreshness: { value: 0 },
    uWriteFilm: { value: 0 },
  };
  const splatMat = new THREE.ShaderMaterial({
    uniforms: splatUniforms,
    vertexShader: SPLAT_VERT,
    fragmentShader: SPLAT_FRAG,
    depthTest: false,
    depthWrite: false,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneFactor,
    transparent: true,
  });
  splatMat.toneMapped = false;

  const ids = new Float32Array(size > 0 ? 1 : 1);
  // Splat geometry is rebuilt when capacity is known; placeholder 1-point until first splat.
  let splatCapacity = 0;
  let splatGeom = new THREE.BufferGeometry();
  splatGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3), 3));
  splatGeom.setAttribute('aId', new THREE.BufferAttribute(ids, 1));
  const splatPoints = new THREE.Points(splatGeom, splatMat);
  splatPoints.frustumCulled = false;
  const splatScene = new THREE.Scene();
  splatScene.add(splatPoints);
  const splatCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const reposeUniforms = {
    tHeight: { value: a.texture },
    tBase: { value: baseTex },
    uTexSize: { value: size },
    uCellSize: { value: cellSize },
    uRelax: { value: 0.5 },
    uSpeckleH: { value: SPECKLE_HEIGHT },
    uDiffuseRate: { value: SPECKLE_DIFFUSE_RATE },
    uFluxCap: { value: CLIFF_FLUX_CAP },
  };
  const reposeMat = new THREE.ShaderMaterial({
    uniforms: reposeUniforms,
    vertexShader: SIM_VERT,
    fragmentShader: REPOSE_FRAG,
    depthTest: false,
    depthWrite: false,
  });
  const fsScene = new THREE.Scene();
  const fsCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const fsMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), reposeMat);
  fsScene.add(fsMesh);

  const previewMat = new THREE.ShaderMaterial({
    uniforms: {
      tHeight: { value: a.texture },
      uMaxH: { value: 1.2 },
    },
    vertexShader: SIM_VERT,
    fragmentShader: HF_PREVIEW_FRAG,
    depthTest: false,
    depthWrite: false,
  });
  const clearMat = new THREE.ShaderMaterial({
    vertexShader: SIM_VERT,
    fragmentShader: CLEAR_FRAG,
    depthTest: false,
    depthWrite: false,
  });
  const composeMat = new THREE.ShaderMaterial({
    uniforms: {
      tHeight: { value: a.texture },
      tDelta: { value: delta.texture },
    },
    vertexShader: SIM_VERT,
    fragmentShader: COMPOSE_FRAG,
    depthTest: false,
    depthWrite: false,
  });
  const volPos = makeFloatTarget(1, THREE.NearestFilter);
  const volMeta = makeFloatTarget(1, THREE.NearestFilter);
  const sampleRt = makeFloatTarget(1, THREE.NearestFilter);
  const sampleMat = new THREE.ShaderMaterial({
    uniforms: {
      tHeight: { value: a.texture },
      uUv: { value: new THREE.Vector2(0.5, 0.5) },
    },
    vertexShader: SIM_VERT,
    fragmentShader: SAMPLE_FRAG,
    depthTest: false,
    depthWrite: false,
  });
  renderer.setRenderTarget(sampleRt);
  renderer.setRenderTarget(prev);

  const shaderClear = (gl: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget) => {
    // gl.clear is unreliable on some half-float configs (NaN/garbage → black ground).
    const prevTarget = gl.getRenderTarget();
    const prevAuto = gl.autoClear;
    const prevMat = fsMesh.material;
    gl.autoClear = true;
    fsMesh.material = clearMat;
    gl.setRenderTarget(target);
    gl.render(fsScene, fsCam);
    fsMesh.material = prevMat;
    gl.setRenderTarget(prevTarget);
    gl.autoClear = prevAuto;
  };
  shaderClear(renderer, a);
  shaderClear(renderer, b);
  shaderClear(renderer, delta);

  const ensureSplatGeom = (capacity: number) => {
    if (capacity <= splatCapacity) return;
    splatCapacity = capacity;
    const pos = new Float32Array(capacity * 3);
    const id = new Float32Array(capacity);
    for (let i = 0; i < capacity; i += 1) id[i] = i;
    splatGeom.dispose();
    splatGeom = new THREE.BufferGeometry();
    splatGeom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    splatGeom.setAttribute('aId', new THREE.BufferAttribute(id, 1));
    splatPoints.geometry = splatGeom;
  };

  return {
    size,
    cellSize,
    cellArea,
    texture() {
      return read.texture;
    },
    baseTexture() {
      return baseTex;
    },
    sampleAt(gl, x, z) {
      const u = (x - arena.minX) / arena.sizeX;
      const v = (z - arena.minZ) / arena.sizeZ;
      const uu = Math.min(1, Math.max(0, u));
      const vv = Math.min(1, Math.max(0, v));
      const buf = new Float32Array(4);
      const prevTarget = gl.getRenderTarget();
      const prevAuto = gl.autoClear;
      const prevMat = fsMesh.material;
      sampleMat.uniforms.tHeight.value = read.texture;
      (sampleMat.uniforms.uUv.value as THREE.Vector2).set(
        (Math.floor(uu * size) + 0.5) / size,
        (Math.floor(vv * size) + 0.5) / size,
      );
      fsMesh.material = sampleMat;
      gl.autoClear = true;
      gl.setRenderTarget(sampleRt);
      gl.render(fsScene, fsCam);
      gl.readRenderTargetPixels(sampleRt, 0, 0, 1, 1, buf);
      fsMesh.material = prevMat;
      gl.setRenderTarget(prevTarget);
      gl.autoClear = prevAuto;
      const granular = Number.isFinite(buf[0]) ? Math.max(0, buf[0]) : 0;
      const film = Number.isFinite(buf[3]) ? Math.max(0, buf[3]) : 0;
      const deposit = granular + film;
      const mix = deposit > 1e-8 && Number.isFinite(buf[1]) ? buf[1] / deposit : 0;
      const base = sampleBaseElevation(baseData, size, arena, x, z);
      return { deposit, granular, film, base, mix, surface: deposit + base };
    },
    splat(gl, posTex, metaTex, grainTexSize, capacity, originTex = null) {
      ensureSplatGeom(capacity);
      splatGeom.setDrawRange(0, capacity);
      splatUniforms.tPos.value = posTex;
      splatUniforms.tMeta.value = metaTex;
      splatUniforms.tOrigin.value = originTex ?? dummyOrigin.texture;
      splatUniforms.tVel.value = dummyVel.texture;
      splatUniforms.uTexSize.value = grainTexSize;
      splatUniforms.uPointSize.value = SPLAT_POINT_SIZE;
      splatUniforms.uVisualGain.value = SPLAT_VISUAL_GAIN;
      splatUniforms.uKernelInvSum.value = SPLAT_KERNEL_INV_SUM;
      splatUniforms.uKernelSharpness.value = SPLAT_KERNEL_SHARPNESS;
      splatUniforms.uSplatMode.value = 0;
      splatUniforms.uFineAttenuate.value = 0;
      splatUniforms.uGrainAttenuate.value = originTex ? 1 : 0;
      splatUniforms.uFreshness.value = 0;
      splatUniforms.uWriteFilm.value = 0;
      const prevTarget = gl.getRenderTarget();
      const prevAuto = gl.autoClear;
      // Zero the delta with a fullscreen write (gl.clear is unreliable on
      // some half-float configs), splat into it, then add onto the field.
      // A fullscreen add does not need float blending.
      gl.autoClear = false;
      fsMesh.material = clearMat;
      gl.setRenderTarget(delta);
      gl.render(fsScene, fsCam);
      gl.render(splatScene, splatCam);
      composeMat.uniforms.tHeight.value = read.texture;
      composeMat.uniforms.tDelta.value = delta.texture;
      fsMesh.material = composeMat;
      gl.autoClear = true;
      gl.setRenderTarget(write);
      gl.render(fsScene, fsCam);
      const tmp = read;
      read = write;
      write = tmp;
      gl.setRenderTarget(prevTarget);
      gl.autoClear = prevAuto;
    },
    splatVolume(gl, x, y, z, mass, materialId = 0) {
      if (mass <= 0) return;
      const pos = new Float32Array([x, y, z, -2]);
      const meta = new Float32Array([0.04, 0, materialId < 0.25 ? 0 : materialId < 0.75 ? 2 : 3, mass]);
      uploadFull(gl, volPos, 1, pos);
      uploadFull(gl, volMeta, 1, meta);
      // Granular (R) stamp: wide flat disk approximating a lying brick
      // (~0.19×0.09 m) at chunk visual gain, not a point spike. Mode 1
      // reads mass from meta.w.
      ensureSplatGeom(1);
      splatGeom.setDrawRange(0, 1);
      splatUniforms.tPos.value = volPos.texture;
      splatUniforms.tMeta.value = volMeta.texture;
      splatUniforms.tOrigin.value = dummyOrigin.texture;
      splatUniforms.tVel.value = dummyVel.texture;
      splatUniforms.uTexSize.value = 1;
      splatUniforms.uPointSize.value = CHUNK_SPLAT_POINT_SIZE;
      splatUniforms.uVisualGain.value = CHUNK_SPLAT_VISUAL_GAIN;
      splatUniforms.uKernelInvSum.value = CHUNK_SPLAT_KERNEL_INV_SUM;
      splatUniforms.uKernelSharpness.value = CHUNK_SPLAT_KERNEL_SHARPNESS;
      splatUniforms.uSplatMode.value = 1;
      splatUniforms.uGrainAttenuate.value = 0;
      splatUniforms.uFineAttenuate.value = 0;
      splatUniforms.uFreshness.value = 1;
      splatUniforms.uWriteFilm.value = 0;
      const prevTarget = gl.getRenderTarget();
      const prevAuto = gl.autoClear;
      gl.autoClear = false;
      fsMesh.material = clearMat;
      gl.setRenderTarget(delta);
      gl.render(fsScene, fsCam);
      gl.render(splatScene, splatCam);
      composeMat.uniforms.tHeight.value = read.texture;
      composeMat.uniforms.tDelta.value = delta.texture;
      fsMesh.material = composeMat;
      gl.autoClear = true;
      gl.setRenderTarget(write);
      gl.render(fsScene, fsCam);
      const tmp = read;
      read = write;
      write = tmp;
      gl.setRenderTarget(prevTarget);
      gl.autoClear = prevAuto;
    },
    splatFines(
      gl,
      posTex,
      metaTex,
      fineTexSize,
      capacity,
      originTex = null,
      velTex = null,
      visualGain: number = FINE_SPLAT_VISUAL_GAIN,
    ) {
      ensureSplatGeom(capacity);
      splatGeom.setDrawRange(0, capacity);
      splatUniforms.tPos.value = posTex;
      splatUniforms.tMeta.value = metaTex;
      const attenuate = originTex != null && velTex != null ? 1 : 0;
      splatUniforms.tOrigin.value = originTex ?? dummyOrigin.texture;
      splatUniforms.tVel.value = velTex ?? dummyVel.texture;
      splatUniforms.uTexSize.value = fineTexSize;
      splatUniforms.uPointSize.value = FINE_SPLAT_POINT_SIZE;
      splatUniforms.uVisualGain.value = visualGain;
      splatUniforms.uKernelInvSum.value = FINE_SPLAT_KERNEL_INV_SUM;
      splatUniforms.uKernelSharpness.value = FINE_SPLAT_KERNEL_SHARPNESS;
      splatUniforms.uSplatMode.value = 1;
      splatUniforms.uGrainAttenuate.value = 0;
      // Live cloud fines: film channel (A). Distance falloff + haze skip
      // when origin+vel are bound. Chunk stamps use splatVolume, not this path.
      splatUniforms.uFineAttenuate.value = attenuate;
      splatUniforms.uFreshness.value = 1;
      splatUniforms.uWriteFilm.value = 1;
      const prevTarget = gl.getRenderTarget();
      const prevAuto = gl.autoClear;
      gl.autoClear = false;
      fsMesh.material = clearMat;
      gl.setRenderTarget(delta);
      gl.render(fsScene, fsCam);
      gl.render(splatScene, splatCam);
      composeMat.uniforms.tHeight.value = read.texture;
      composeMat.uniforms.tDelta.value = delta.texture;
      fsMesh.material = composeMat;
      gl.autoClear = true;
      gl.setRenderTarget(write);
      gl.render(fsScene, fsCam);
      const tmp = read;
      read = write;
      write = tmp;
      gl.setRenderTarget(prevTarget);
      gl.autoClear = prevAuto;
    },
    repose(gl) {
      reposeUniforms.tHeight.value = read.texture;
      const prevTarget = gl.getRenderTarget();
      const prevAuto = gl.autoClear;
      gl.autoClear = true;
      fsMesh.material = reposeMat;
      gl.setRenderTarget(write);
      gl.render(fsScene, fsCam);
      gl.setRenderTarget(prevTarget);
      gl.autoClear = prevAuto;
      const tmp = read;
      read = write;
      write = tmp;
    },
    reset(gl) {
      shaderClear(gl, a);
      shaderClear(gl, b);
      shaderClear(gl, delta);
      read = a;
      write = b;
    },
    blitPreview(gl, canvas) {
      previewMat.uniforms.tHeight.value = read.texture;
      fsMesh.material = previewMat;
      const prevTarget = gl.getRenderTarget();
      const prevAuto = gl.autoClear;
      gl.autoClear = true;
      gl.setRenderTarget(preview);
      gl.render(fsScene, fsCam);
      const w = preview.width;
      const h = preview.height;
      const buf = new Float32Array(w * h * 4);
      gl.readRenderTargetPixels(preview, 0, 0, w, h, buf);
      gl.setRenderTarget(prevTarget);
      gl.autoClear = prevAuto;
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const img = ctx.createImageData(w, h);
      // WebGL is y-up; flip for 2D canvas.
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const src = ((h - 1 - y) * w + x) * 4;
          const dst = (y * w + x) * 4;
          img.data[dst] = Math.min(255, Math.max(0, buf[src] * 255));
          img.data[dst + 1] = Math.min(255, Math.max(0, buf[src + 1] * 255));
          img.data[dst + 2] = Math.min(255, Math.max(0, buf[src + 2] * 255));
          img.data[dst + 3] = 255;
        }
      }
      ctx.putImageData(img, 0, 0);
    },
    dispose() {
      a.dispose();
      b.dispose();
      delta.dispose();
      preview.dispose();
      splatMat.dispose();
      reposeMat.dispose();
      baseRt.dispose();
      previewMat.dispose();
      composeMat.dispose();
      clearMat.dispose();
      sampleMat.dispose();
      sampleRt.dispose();
      volPos.dispose();
      volMeta.dispose();
      dummyOrigin.dispose();
      dummyVel.dispose();
      splatGeom.dispose();
      fsMesh.geometry.dispose();
    },
  };
}
